import { BehaviorSubject, Subject } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { WINDOW_TOKEN } from '../../../services/bootstrap.service';
import type { OnDestroy } from '@angular/core';
import type { Observable, Subscription } from 'rxjs';
import type { AccessibleErrors } from '../domain/form.domain';

/**
 * A gatekeeper service that aggregates form errors from potentially nested forms and provides them to the
 * first form-accessibility-alert component that registers itself with it.
 * */
@Injectable({ providedIn: 'root' })
export class FormAccessibilityService implements OnDestroy {
  private readonly browserWindow = inject<Window>(WINDOW_TOKEN);

  get shouldSubscribeToModalAlerts(): boolean {
    return this._shouldSubscribeToModalAlerts;
  }

  set shouldSubscribeToModalAlerts(value: boolean) {
    this._shouldSubscribeToModalAlerts = value;
  }

  get shouldSubscribeToPageAlerts(): boolean {
    return this._shouldSubscribeToPageAlerts;
  }

  set shouldSubscribeToPageAlerts(value: boolean) {
    this._shouldSubscribeToPageAlerts = value;
  }

  static readonly ERROR_KEY_SUFFIX: string = '-accessible-error';

  private readonly pageAccessibleErrors = new Map<Observable<AccessibleErrors>, AccessibleErrors>();
  private readonly pageErrorSubscriptions = new Map<Observable<AccessibleErrors>, Subscription>();
  private pageAlertSubject: BehaviorSubject<AccessibleErrors> = new BehaviorSubject<AccessibleErrors>({});
  private readonly destroyPageAlert: Subject<void> = new Subject<void>();
  private currentPageErrors: AccessibleErrors = {};

  private readonly modalAccessibleErrors = new Map<Observable<AccessibleErrors>, AccessibleErrors>();
  private readonly modalErrorSubscriptions = new Map<Observable<AccessibleErrors>, Subscription>();
  private modalAlertSubject: BehaviorSubject<AccessibleErrors> = new BehaviorSubject<AccessibleErrors>({});
  private readonly destroyModalAlert: Subject<void> = new Subject<void>();
  private currentModalErrors: AccessibleErrors = {};

  private _shouldSubscribeToPageAlerts: boolean = true;
  private _shouldSubscribeToModalAlerts: boolean = true;

  static getErrorKey(inputName: string): string {
    return inputName + FormAccessibilityService.ERROR_KEY_SUFFIX;
  }

  isModal(): boolean {
    return this.browserWindow.document.querySelector('td-wb-modal') !== null;
  }

  register(isModalAlert: boolean): Observable<AccessibleErrors> {
    if (isModalAlert && this.shouldSubscribeToModalAlerts) {
      this.shouldSubscribeToModalAlerts = false;
      return this.modalAlertSubject;
    } else if (this.shouldSubscribeToPageAlerts) {
      this.shouldSubscribeToPageAlerts = false;
      return this.pageAlertSubject;
    }
  }

  registerErrors(errors: Observable<AccessibleErrors>, isModalForm: boolean = false): void {
    const subscriptions = isModalForm ? this.modalErrorSubscriptions : this.pageErrorSubscriptions;
    if (subscriptions.has(errors)) {
      return;
    }
    const subscription = errors
      .pipe(debounceTime(100), takeUntil(isModalForm ? this.destroyModalAlert : this.destroyPageAlert))
      .subscribe((value: AccessibleErrors): void => this.updateErrors(errors, value, isModalForm));
    subscriptions.set(errors, subscription);
  }

  unregisterErrors(isModalForm: boolean, errors?: Observable<AccessibleErrors>): void {
    const values = isModalForm ? this.modalAccessibleErrors : this.pageAccessibleErrors;
    const subscriptions = isModalForm ? this.modalErrorSubscriptions : this.pageErrorSubscriptions;
    if (errors === undefined) {
      subscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
      subscriptions.clear();
      values.clear();
    } else {
      subscriptions.get(errors)?.unsubscribe();
      subscriptions.delete(errors);
      values.delete(errors);
    }
    this.publishAggregatedErrors(isModalForm);
  }

  getVisibleErrorCount(isModal: boolean): number {
    let errorCount = 0;
    if (isModal) {
      for (const error in this.currentModalErrors) {
        if (this.currentModalErrors[error].isDisplayed) {
          errorCount++;
        }
      }
    } else {
      for (const error in this.currentPageErrors) {
        if (this.currentPageErrors[error].isDisplayed) {
          errorCount++;
        }
      }
    }
    return errorCount;
  }

  scrollErrorIntoView(isModalForm: boolean = false): void {
    if (isModalForm && this.currentModalErrors !== undefined) {
      const errorKey = Object.keys(this.currentModalErrors)[0];
      const error = this.currentModalErrors[errorKey];
      if (error !== undefined) {
        if (!error.isInViewPort()) {
          error.scrollIntoView();
        } else {
          error.focusInput();
        }
      }
    } else if (this.currentPageErrors !== undefined) {
      const errorKey = Object.keys(this.currentPageErrors)[0];
      const error = this.currentPageErrors[errorKey];
      if (error !== undefined) {
        if (!error.isInViewPort()) {
          error.scrollIntoView();
        } else {
          error.focusInput();
        }
      }
    }

  }

  ngOnDestroy(): void {
    this.destroyPageAlert.next();
    this.destroyPageAlert.complete();
    this.destroyModalAlert.next();
    this.destroyModalAlert.complete();
    this.pageErrorSubscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
    this.modalErrorSubscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
  }

  private updateErrors(source: Observable<AccessibleErrors>, errors: AccessibleErrors, isModal: boolean): void {
    const values = isModal ? this.modalAccessibleErrors : this.pageAccessibleErrors;
    values.set(source, errors);
    this.publishAggregatedErrors(isModal);
  }

  private publishAggregatedErrors(isModal: boolean): void {
    const values = isModal ? this.modalAccessibleErrors : this.pageAccessibleErrors;
    const incomingErrors = Object.assign({}, ...values.values()) as AccessibleErrors;
    const currentErrors = isModal ? this.currentModalErrors : this.currentPageErrors;
    if (JSON.stringify(currentErrors) === JSON.stringify(incomingErrors)) {
      return;
    }
    if (isModal) {
      this.currentModalErrors = incomingErrors;
      this.modalAlertSubject.next(incomingErrors);
    } else {
      this.currentPageErrors = incomingErrors;
      this.pageAlertSubject.next(incomingErrors);
    }
  }
}
